/**
 * Auth Store v2 — JWT-only truth source, transaction-ized login.
 *
 * ★ Auth v2 Contract (FROZEN 2026-07-28):
 *   1. 真相源唯一: HttpOnly JWT Cookie — 不存在 localStorage['logged_in']
 *   2. 登录事务化: POST /login → GET /auth/session → GET /auth/permissions
 *      全部成功后 commit; 任一失败 rollback
 *   3. 路由守卫: GET /api/auth/session.authenticated 是唯一登录判断
 *   4. 禁止: localStorage boolean / setTimeout redirect / 多端点冲突
 *
 * Endpoints:
 *   GET /api/auth/session      → { authenticated, user_id, username, role, school_id }
 *   GET /api/auth/permissions  → { roles, permissions, is_admin }
 *   GET /api/teacher/context   → { class_ids, classes, subjects, grades }
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import apiClient from '@/api'

// ─── Auth State (no localStorage-derived defaults) ───

export interface AuthSession {
  authenticated: boolean
  user_id: string
  username: string
  teacher_name: string
  role: string
  is_admin: boolean
  school_id: number | null
  school_name: string
}

export interface AuthPermissions {
  roles: string[]
  permissions: string[]
  is_admin: boolean
}

export interface TeacherContext {
  teacher_name: string
  class_ids: number[]
  classes: Array<{ id: number; name: string; grade: string }>
  subjects: string[]
  grades: string[]
  homeroom_class_ids: number[]
}

// keep TeacherScope for backward compat with components that reference it
export interface TeacherScope {
  is_admin: boolean
  roles: string[]
  permissions: string[]
  class_ids: number[]
  homeroom_class_ids: number[]
  subjects: string[]
  grades: string[]
  teacher_name: string
}

const EMPTY_SESSION: AuthSession = {
  authenticated: false, user_id: '', username: '', teacher_name: '',
  role: '', is_admin: false, school_id: null, school_name: '',
}

const EMPTY_PERMISSIONS: AuthPermissions = {
  roles: [], permissions: [], is_admin: false,
}

const EMPTY_CONTEXT: TeacherContext = {
  teacher_name: '', class_ids: [], classes: [], subjects: [], grades: [], homeroom_class_ids: [],
}

/** Legacy scope built from v2 primitives for backward compat */
function _buildScope(session: AuthSession, perms: AuthPermissions, ctx: TeacherContext): TeacherScope {
  return {
    is_admin: session.is_admin || perms.is_admin,
    roles: perms.roles,
    permissions: perms.permissions,
    class_ids: ctx.class_ids,
    homeroom_class_ids: ctx.homeroom_class_ids,
    subjects: ctx.subjects,
    grades: ctx.grades,
    teacher_name: session.teacher_name || ctx.teacher_name,
  }
}

export const useAuthStore = defineStore('auth', () => {
  // ─── State — initialized empty, filled by initialize() ───
  const session = ref<AuthSession>({ ...EMPTY_SESSION })
  const permissions = ref<AuthPermissions>({ ...EMPTY_PERMISSIONS })
  const context = ref<TeacherContext>({ ...EMPTY_CONTEXT })

  // ★ Auth status state machine: loading → authenticated | unauthenticated
  type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'
  const authStatus = ref<AuthStatus>('loading')

  // ★ P0: 防重复调用锁
  let _initPromise: Promise<boolean> | null = null

  // Legacy compat — built from v2 primitives
  const scope = computed<TeacherScope>(() => _buildScope(session.value, permissions.value, context.value))
  const scopeLoaded = ref(false)

  // Derived
  const teacherId = computed(() => session.value.user_id)
  const teacherName = computed(() => session.value.teacher_name)
  const teacherRole = computed(() => session.value.role)
  const isAdmin = computed(() => session.value.is_admin || permissions.value.is_admin)
  const isAuthenticated = computed(() => session.value.authenticated)
  const schoolName = computed(() => session.value.school_name || teacherSchool.value || '')

  const teacherSchool = ref('')
  const teacherSubject = ref('')
  const showProfileMenu = ref(false)
  const isOnline = ref(navigator.onLine !== false)
  const offlineQueueCount = ref(0)
  const loginLoading = ref(false)

  // ─── Computed ───────────────────────────────────
  const roleLabels: Record<string, string> = {
    admin: '管理员', school_admin: '学校管理员', director: '年级主任',
    head_teacher: '班主任', subject_leader: '学科组长', teacher: '教师',
  }

  const displayRole = computed(() => {
    if (isAdmin.value) return '管理员'
    const roles = permissions.value.roles
    if (roles.includes('school_admin')) return '学校管理员'
    const priority = ['director', 'head_teacher', 'subject_leader', 'teacher']
    for (const r of priority) {
      if (roles.includes(r)) return roleLabels[r] || r
    }
    return session.value.role || '教师'
  })

  // ─── ★ v2: initialize — 防重复调用, authStatus 状态机 ───
  async function initialize(): Promise<boolean> {
    // 已初始化 → 直接用缓存
    if (scopeLoaded.value) return session.value.authenticated
    // 正在初始化 → 复用进行中的 Promise
    if (_initPromise) return _initPromise

    authStatus.value = 'loading'
    _initPromise = _doInit()
    try {
      const ok = await _initPromise
      authStatus.value = ok ? 'authenticated' : 'unauthenticated'
      return ok
    } finally {
      _initPromise = null
    }
  }

  async function _doInit(): Promise<boolean> {
    try {
      const sessRes = await apiClient.get('/auth/session')
      const sess = sessRes.data as AuthSession
      if (!sess.authenticated) {
        _reset()
        return false
      }
      session.value = sess

      // Fetch permissions + context in parallel
      const [permRes, ctxRes] = await Promise.allSettled([
        apiClient.get('/auth/permissions'),
        apiClient.get('/teacher/context'),
      ])

      if (permRes.status === 'fulfilled') {
        permissions.value = permRes.value.data as AuthPermissions
      }
      if (ctxRes.status === 'fulfilled') {
        context.value = ctxRes.value.data as TeacherContext
      }

      scopeLoaded.value = true
      return true
    } catch {
      _reset()
      return false
    }
  }

  // ─── ★ v2: login — 事务化 ───
  async function login(username: string, password: string): Promise<boolean> {
    loginLoading.value = true
    try {
      // Step 1: POST /auth/login → get JWT cookie
      const res = await authApi.login(username, password)
      if (!res.success) return false

      // Step 2: GET /auth/session → verify identity
      const sessRes = await apiClient.get('/auth/session')
      const sess = sessRes.data as AuthSession
      if (!sess.authenticated) {
        _reset()
        return false
      }

      // Step 3: GET /auth/permissions + /teacher/context in parallel
      const [permRes, ctxRes] = await Promise.allSettled([
        apiClient.get('/auth/permissions'),
        apiClient.get('/teacher/context'),
      ])

      // ★ All three succeeded → commit atomically
      session.value = sess
      permissions.value = permRes.status === 'fulfilled'
        ? (permRes.value.data as AuthPermissions)
        : { ...EMPTY_PERMISSIONS, is_admin: sess.is_admin, roles: ['admin'], permissions: ['*'] }
      context.value = ctxRes.status === 'fulfilled'
        ? (ctxRes.value.data as TeacherContext)
        : { ...EMPTY_CONTEXT }
      scopeLoaded.value = true

      // Cache teacher_id only for X-Teacher-Id header (not auth decision)
      try { localStorage.setItem('teacher_id', sess.user_id) } catch { /* ignore */ }
      return true
    } catch {
      _reset()
      return false
    } finally {
      loginLoading.value = false
    }
  }

  async function logout() {
    try { await authApi.logout({}) } catch { /* ignore */ }
    sessionStorage.removeItem('rbac_scope')
    _reset()
    window.location.replace('/v2/login')
  }

  // ─── ★ v2: scope loaded → fallback compat with old code expecting scope ───
  async function fetchScope(): Promise<TeacherScope> {
    const ok = await initialize()
    if (ok) return scope.value
    // failed → return empty scope, router will redirect
    return _buildScope(EMPTY_SESSION, EMPTY_PERMISSIONS, EMPTY_CONTEXT)
  }

  // ─── Permissions ─────────────────────────────────
  function hasPermission(permission: string): boolean {
    if (isAdmin.value) return true
    const perms = permissions.value.permissions
    if (perms.includes('*')) return true
    if (perms.includes(permission)) return true
    const [prefix] = permission.split(':')
    if (perms.includes(`${prefix}:*`)) return true
    return false
  }

  function hasRole(role: string): boolean {
    if (role === 'admin') return isAdmin.value
    return permissions.value.roles.includes(role)
  }

  function canAccessClass(classId: number): boolean {
    if (isAdmin.value) return true
    return context.value.class_ids.includes(classId)
  }

  function canAccessSubject(subject: string): boolean {
    if (isAdmin.value) return true
    return context.value.subjects.includes(subject)
  }

  function canAccessGeometryLab(): boolean {
    if (isAdmin.value) return true
    const subs = context.value.subjects
    // ★ P1 fix: 移除学段限制 (原要求初中/高中), 所有含数学学科的用户均可访问
    const hasMath = subs.length === 0 || subs.some(s => s.includes('数学'))
    return hasMath
  }

  // ─── Cleanup ─────────────────────────────────────
  function _reset() {
    session.value = { ...EMPTY_SESSION }
    permissions.value = { ...EMPTY_PERMISSIONS }
    context.value = { ...EMPTY_CONTEXT }
    scopeLoaded.value = false
    authStatus.value = 'unauthenticated'
    _initPromise = null
    ;['logged_in','teacher_id','teacher_name','teacher_school','teacher_username',
      'teacher_role','teacher_subject','is_admin','last_school_id','last_school_name',
    ].forEach(k => { try { localStorage.removeItem(k) } catch { /* ignore */ } })
  }

  function clearLoginState() { _reset() }

  // legacy
  async function checkAuth(): Promise<boolean> { return initialize() }

  async function refreshToken(): Promise<string | null> {
    try {
      const resp = await authApi.refresh('')
      if (resp.success) {
        await initialize()
        return 'ok'
      }
    } catch { _reset(); window.location.replace('/v2/login') }
    return null
  }

  // ─── Online/Offline ───────────────────────────────
  if (!(window as any).__auth_listeners_registered) {
    (window as any).__auth_listeners_registered = true
    window.addEventListener('online', () => { isOnline.value = true })
    window.addEventListener('offline', () => { isOnline.value = false })
  }

  return {
    // State
    session, permissions, context, scope, scopeLoaded, authStatus,
    teacherId, teacherName, teacherSchool, teacherSubject, teacherRole, schoolName,
    isAdmin, isAuthenticated, showProfileMenu, isOnline, offlineQueueCount, loginLoading,
    displayRole,
    // Actions
    initialize, login, logout, checkAuth, refreshToken, clearLoginState, fetchScope,
    // Permission checks
    hasPermission, hasRole, canAccessClass, canAccessSubject, canAccessGeometryLab,
  }
})
